import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Box,
  Typography,
  Button,
  Divider
} from "@material-ui/core";
import { connect } from "react-redux";
import { withSnackbar } from "notistack";
import BillingDetails from "./BillingDetails";
import { useHistory, Route, Link as RouterLink } from "react-router-dom";
import DateFnsUtils from "@date-io/date-fns";
import { capitalize } from "../../../utils/helpers";
import { plans } from "../../data/plans";
import Skeleton from "@material-ui/lab/Skeleton";
import { currencies } from "../../data/constants";

const useStyles = makeStyles(theme => ({
  overviewSection: {
    padding: theme.spacing(4, 0, 3)
  },
  text: {
    lineHeight: "1.1875rem",
    color: "#808080",
    marginBottom: theme.spacing(0.5),
    letterSpacing: "-0.3px",
    "&:last-child": {
      marginBottom: "0"
    },
    "& span": {
      display: "block"
    },
    "& span + span": {
      paddingTop: theme.spacing(0.5)
    }
  },
  btn: {
    minWidth: 0,
    padding: theme.spacing(0.75, 2),
    marginRight: theme.spacing(-2)
  },
  animationWrapper: {
    backgroundColor: "#fff"
  },
  formWrapper: {
    overflow: "hidden",
    boxSizing: "border-box",
    transition: theme.transitions.create(["padding"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  tilte: {
    fontWeight: 500,
    lineHeight: "1.1875rem"
  },
  subtitle: {
    fontSize: "0.875rem",
    lineHeight: "1.1875rem",
    marginBottom: theme.spacing(1)
  },
  divider: {
    backgroundColor: "#EEE"
  }
}));

function BillingOverview({
  drawerExpanded,
  setActiveDialog,
  vehiclesCount,
  billEstimate,
  billEstimateLoaded,
  billingDetails,
  paymentMethod,
  organisation,
  setLoading
}) {
  const plan = organisation.plan;
  const subscribeStatus = organisation.subscription_status;

  const classes = useStyles();

  const history = useHistory();

  const [paymentDetails, setPaymentDetails] = useState({
    expMonth: "",
    expYear: "",
    createDate: ""
  });

  useEffect(() => {
    // Extract payment details
    if (
      paymentMethod &&
      (!paymentMethod.pm_type || paymentMethod.pm_type === "card")
    ) {
      const dateFns = new DateFnsUtils();
      let expMonth, expYear, createDate;
      expMonth =
        paymentMethod.exp_month >= 10
          ? paymentMethod.exp_month
          : "0" + paymentMethod.exp_month;
      expYear = paymentMethod.exp_year.toString().slice(2);
      createDate = dateFns.format(
        new Date(paymentMethod.created_at * 1000),
        "dd/MM/yyyy"
      );
      setPaymentDetails({ expMonth, expYear, createDate });
    }
  }, [paymentMethod]);

  const incompleteAccount =
    organisation &&
    organisation.subscription_last_payment_status ===
      "requires_payment_method" &&
    organisation.subscription_status === "incomplete";

  const incompletePayment =
    organisation &&
    organisation.subscription_last_payment_status === "requires_action" &&
    organisation.subscription_status === "incomplete";

  const currentPlan = plans.find(planData => planData.name === plan.type) || {};

  const perVehiclePerMonth = plan && currentPlan ? currentPlan.price : "";
  const monthlyEstimate =
    plan && vehiclesCount ? perVehiclePerMonth * vehiclesCount : "";

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        height="100%"
        style={{ boxSizing: "border-box" }}
      >
        {!plan.type ? (
          <Box mt={3}>
            <Skeleton height={28} width={"75%"} />
            <Skeleton />
            <Skeleton />
            <Box py={2}>
              <Divider />
            </Box>
            <Skeleton height={28} width={"75%"} />
            <Skeleton />
            <Skeleton />
          </Box>
        ) : (
          <>
            {plan.type && (
              <>
                <Box className={classes.overviewSection} data-cy="billing-plan">
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Typography className={classes.tilte}>
                      {"Current plan"}
                    </Typography>

                    <Button
                      className={classes.btn}
                      onClick={() => {
                        setActiveDialog("");
                        history.push("/payment/select-plan", {
                          from: "billing"
                        });
                      }}
                      color="primary"
                      disabled={incompleteAccount || incompletePayment}
                    >
                      {subscribeStatus === "trialing" && !paymentMethod
                        ? "Upgrade plan"
                        : "Modify plan"}
                    </Button>
                  </Box>

                  <Typography
                    className={classes.text}
                    data-cy="billingPlanTitle"
                  >
                    {currentPlan.title || capitalize(plan.type)}{" "}
                    {subscribeStatus === "trialing" &&
                      !paymentMethod &&
                      "(Trial)"}
                  </Typography>
                </Box>
                <Divider className={classes.divider} />
              </>
            )}
            <Box className={classes.overviewSection} data-cy="billing-pm">
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography className={classes.tilte}>
                  Payment method
                </Typography>
                {paymentMethod && subscribeStatus !== "canceled" && (
                  <Button
                    className={classes.btn}
                    onClick={() => {
                      setActiveDialog("");
                      history.push("/payment/edit-payment-method", {
                        action: "edit",
                        from: "billing"
                      });
                    }}
                    color="primary"
                  >
                    {"Edit payment method"}
                  </Button>
                )}
              </Box>

              <Typography className={classes.text}>
                {paymentMethod ? (
                  !paymentMethod.pm_type || paymentMethod.pm_type === "card" ? (
                    <>
                      <span>
                        Ending in <b>{paymentMethod.last4}</b>
                      </span>
                      <span>
                        Expires {paymentDetails.expMonth}/
                        {paymentDetails.expYear} - Added{" "}
                        {paymentDetails.createDate}
                      </span>
                    </>
                  ) : paymentMethod.pm_type === "sepa_debit" ? (
                    <>
                      <span>
                        Bank account <b>•••• {paymentMethod.last4}</b>
                      </span>
                      <span>{billingDetails.name}</span>
                    </>
                  ) : null
                ) : (
                  <span>
                    No payment method added. After upgrading your plan, your
                    payment method will be visible on this page.
                  </span>
                )}
              </Typography>
            </Box>
            <Divider className={classes.divider} />

            <>
              <Box
                className={classes.overviewSection}
                data-cy="billing-details"
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography className={classes.tilte}>
                    Billing details
                  </Typography>
                  <Button
                    className={classes.btn}
                    component={RouterLink}
                    to="/billing/edit-billing-details"
                    color="primary"
                  >
                    {" "}
                    Edit billing details
                  </Button>
                </Box>
                <>
                  <Box mb={billingDetails.address_line1 ? 3 : 0}>
                    <Typography className={classes.subtitle}>
                      Billing contact
                    </Typography>
                    <Typography className={classes.text} data-cy="billingEmail">
                      {billingDetails.email}
                    </Typography>
                  </Box>
                  <Box>
                    {billingDetails.address_line1 && (
                      <>
                        <Typography className={classes.subtitle}>
                          Billing address
                        </Typography>
                        <Typography className={classes.text}>
                          {billingDetails.address_line1}
                        </Typography>
                        {billingDetails.address_line2 && (
                          <Typography className={classes.text}>
                            {billingDetails.address_line2}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                </>
              </Box>
              <Divider className={classes.divider} />
            </>

            {!!billEstimate
              ? !!monthlyEstimate &&
                !!paymentMethod && (
                  <>
                    <Box
                      className={classes.overviewSection}
                      data-cy="billing-estimate"
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                        py={0.75}
                      >
                        <Typography className={classes.tilte}>
                          Bill estimate
                        </Typography>
                      </Box>

                      <>
                        <Box mb={3}>
                          <Typography className={classes.subtitle}>
                            Number of vehicles
                          </Typography>
                          <Typography
                            className={classes.text}
                            data-cy="billingVehiclesCount"
                          >
                            {vehiclesCount}
                          </Typography>
                        </Box>
                        <Box mb={3}>
                          <Typography className={classes.subtitle}>
                            Monthly estimate
                          </Typography>
                          <Typography className={classes.text}>
                            {currencies[plan.currency?.toUpperCase()].symbol}
                            {monthlyEstimate},00
                          </Typography>
                        </Box>
                        <Box>
                          <Typography className={classes.subtitle}>
                            Next billing date
                          </Typography>
                          <Typography
                            className={classes.text}
                            data-cy="billingDate"
                          >
                            {new Date(
                              billEstimate.next_payment_attempt * 1000
                            ).toLocaleString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric"
                            })}
                          </Typography>
                        </Box>
                      </>
                    </Box>
                    <Divider className={classes.divider} />
                  </>
                )
              : paymentMethod &&
                !billEstimateLoaded && (
                  <Box mt={3}>
                    <Skeleton height={28} width={"75%"} />
                    <Skeleton />
                    <Skeleton />
                  </Box>
                )}
          </>
        )}
      </Box>

      <Route path="/billing/edit-billing-details">
        <Box
          position="absolute"
          left="0"
          right="0"
          top="0"
          minHeight={"100vh"}
          zIndex={50}
          className={classes.animationWrapper}
        >
          <Box
            className={classes.formWrapper}
            style={{ paddingLeft: drawerExpanded ? 280 : 56 }}
            display="flex"
            flexDirection="column"
            height="100%"
            position="absolute"
          >
            <BillingDetails setLoading={setLoading} />
          </Box>
        </Box>
      </Route>
    </>
  );
}

const mapStateToProps = state => ({
  vehiclesCount: state.project.vehicleTypes.length,
  paymentMethod: state.account.payment,
  billingDetails: state.account.billingDetails,
  organisation:
    (state.auth.userData.organisationSite &&
      state.auth.userData.organisationSite.organisation) ||
    {}
});

export default connect(mapStateToProps)(withSnackbar(BillingOverview));
